import { ErrorState } from '@/components/primitives/ErrorState'
import { useRecordSheet } from '@/components/record-sheet/context'
import { btnPrimary, iconBtn } from '@/components/record-sheet/styles'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import {
  type ChatRoomSummary,
  type TeamAgent,
  createRoom,
  fetchRoom,
  fetchRooms,
  fetchTeam,
  sendMessage,
  stopRoom
} from '@/lib/ai-team'
import { notifyError, queryClient } from '@/lib/query'
import { fetchMemory } from '@/lib/team-memory'
import { buildWorkflow, fetchWorkflow } from '@/lib/workflows'
import { cn } from '@/lib/utils'
import { useQuery } from '@tanstack/react-query'
import {
  ArrowLeft,
  ArrowUp,
  Brain,
  ChevronDown,
  LayoutGrid,
  PencilLine,
  Loader2,
  MessagesSquare,
  PanelLeftClose,
  PanelLeftOpen,
  PanelRight,
  Plus,
  Settings,
  Square,
  Users,
  Workflow as WorkflowIcon,
  X,
  Zap
} from 'lucide-react'
import { type KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  AgentAvatar,
  ChiefOrb,
  type OrbState,
  STATE_LABEL
} from './AgentAvatar'
import { DecisionDock } from './DecisionDock'
import { MessageRow } from './ChatMessages'
import { ChatRoomMenu } from './ChatRoomMenu'
import { ChatSidePanel } from './ChatSidePanel'
import { ThinkingRow, WorkBlock, groupMessages } from './ChatWork'
import { WorkflowPanelContext } from './workflows/ChatWorkflowCard'
import { WorkflowCanvas } from './workflows/WorkflowCanvasPage'
import { TemplatePicker } from './workflows/TemplateGallery'

const BASE = '/apps/ai-team/chat'

/** Where closing the chat goes: the AI team page it was opened from (set by the workspace). */
function chatReturnPath() {
  try {
    return sessionStorage.getItem('ai-team-return') || '/apps/ai-team'
  } catch {
    return '/apps/ai-team'
  }
}

const HISTORY_KEY = 'ai-team:chat-history-open'

export function ChatPage() {
  const { roomId } = useParams()
  const navigate = useNavigate()
  // The chat list can be folded away for more room; the choice is remembered per browser.
  const [historyOpen, setHistoryOpen] = useState(() => {
    try {
      return localStorage.getItem(HISTORY_KEY) !== '0'
    } catch {
      return true
    }
  })
  const toggleHistory = () =>
    setHistoryOpen((v) => {
      try {
        localStorage.setItem(HISTORY_KEY, v ? '0' : '1')
      } catch {
        /* storage blocked: still toggles for this visit */
      }
      return !v
    })
  const rooms = useQuery({
    queryKey: ['ai-rooms'],
    queryFn: fetchRooms,
    refetchInterval: 10_000
  })
  const team = useQuery({
    queryKey: ['ai-team'],
    queryFn: fetchTeam,
    staleTime: 60_000
  })
  const activeId = roomId ? Number(roomId) : rooms.data?.team.id

  async function startChat(agent?: TeamAgent) {
    try {
      const room = await createRoom(agent?.key)
      await queryClient.invalidateQueries({ queryKey: ['ai-rooms'] })
      navigate(`${BASE}/${room.id}`)
    } catch (err) {
      notifyError('Could not start the chat', err)
    }
  }

  return (
    <div className="-m-6 flex h-[calc(100dvh-5.5rem)] min-h-[480px] gap-3 overflow-hidden bg-neutral-100 p-3 dark:bg-neutral-950">
      <aside
        className={cn(
          'w-full shrink-0 flex-col overflow-hidden md:w-72',
          'rounded-[36px] border border-neutral-200 bg-white dark:border-neutral-800 dark:bg-black',
          historyOpen ? 'md:flex' : 'md:hidden',
          roomId ? 'hidden' : 'flex'
        )}
      >
        <div className="flex items-center justify-between px-5 pb-1 pt-4">
          <p className="text-base font-bold text-neutral-900 dark:text-white">
            Chats
          </p>
        </div>
        <div className="flex gap-2 px-3 pb-3 pt-2">
          <button
            type="button"
            className={cn(btnPrimary, 'flex-1 justify-center')}
            onClick={() => startChat()}
          >
            <Plus className="size-4" /> New chat
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className={iconBtn}
                aria-label="Chat with one agent"
                title="Chat with one agent"
              >
                <ChevronDown className="size-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="max-h-[60vh] w-64 overflow-auto rounded-[36px]"
            >
              <DropdownMenuLabel>One-to-one with…</DropdownMenuLabel>
              {(team.data?.agents ?? []).map((a) => (
                <DropdownMenuItem
                  key={a.key}
                  onSelect={() => startChat(a)}
                  className="gap-2"
                >
                  <span className="truncate">{a.name}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <nav
          className="flex-1 space-y-1 overflow-y-auto px-2 pb-2"
          aria-label="Chats"
        >
          {rooms.isLoading &&
            [0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-14 animate-pulse rounded-[36px] bg-neutral-200 dark:bg-neutral-800"
              />
            ))}
          {rooms.data && (
            <>
              <div className="rounded-[24px] bg-neutral-100 p-1 dark:bg-neutral-900">
                <p className="px-3 pb-1 pt-2 text-[11px] font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
                  Pinned
                </p>
                <RoomLink
                  room={rooms.data.team}
                  active={activeId === rooms.data.team.id}
                />
                <RoomLink
                  room={rooms.data.automations}
                  active={activeId === rooms.data.automations.id}
                />
              </div>
              <div className="mt-3 rounded-[24px] bg-neutral-100 p-1 dark:bg-neutral-900">
                <p className="px-3 pb-1 pt-2 text-[11px] font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
                  Recent
                  {rooms.data.rooms.length > 0 && (
                    <span className="ml-1.5 tabular-nums">
                      {rooms.data.rooms.length}
                    </span>
                  )}
                </p>
                {rooms.data.rooms.length === 0 && (
                  <p className="px-3 pb-3 text-xs text-neutral-500 dark:text-neutral-400">
                    Each topic gets its own chat: press "New chat" to start fresh.
                    Chief of Staff brings in the right agents.
                  </p>
                )}
                <div className="space-y-0.5">
                  {rooms.data.rooms.map((r) => (
                    <RoomLink key={r.id} room={r} active={activeId === r.id} />
                  ))}
                </div>
              </div>
            </>
          )}
        </nav>
      </aside>

      <section
        className={cn(
          'min-w-0 flex-1 flex-col',
          roomId ? 'flex' : 'hidden md:flex'
        )}
      >
        {rooms.error && (
          <ErrorState error={rooms.error} onRetry={() => rooms.refetch()} />
        )}
        {activeId ? (
          <Conversation
            key={activeId}
            roomId={activeId}
            agents={team.data?.agents ?? []}
            historyOpen={historyOpen}
            onToggleHistory={toggleHistory}
            onNewChat={() => startChat()}
          />
        ) : (
          !rooms.error && (
            <div className="flex flex-1 items-center justify-center rounded-[36px] border border-neutral-200 bg-white dark:border-neutral-800 dark:bg-black">
              <Loader2 className="size-5 animate-spin text-neutral-400" />
            </div>
          )
        )}
      </section>
    </div>
  )
}

function RoomLink({
  room,
  active
}: {
  room: ChatRoomSummary
  active: boolean
}) {
  const navigate = useNavigate()
  const isTeam = room.kind === 'team'
  const isFeed = room.kind === 'feed'
  const isGroup = room.kind === 'group'
  return (
    <div className="group relative">
      <button
        type="button"
        onClick={() => navigate(`${BASE}/${room.id}`)}
        className={cn(
          'flex w-full items-center gap-3 rounded-[36px] px-3 py-2 pr-10 text-left transition-colors',
          active
            ? 'bg-white shadow-sm ring-1 ring-neutral-200 dark:bg-neutral-800 dark:ring-neutral-700'
            : 'hover:bg-white dark:hover:bg-neutral-800'
        )}
      >
        {isTeam ? (
          <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-red-500 text-white">
            <Users className="size-4" />
          </span>
        ) : isFeed ? (
          <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-neutral-900 text-white dark:bg-white dark:text-black">
            <Zap className="size-4" />
          </span>
        ) : (
          <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-neutral-200 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-200">
            {isGroup ? (
              <MessagesSquare className="size-4" />
            ) : (
              <span className="text-sm font-semibold">
                {room.agent?.name.charAt(0) ?? '?'}
              </span>
            )}
          </span>
        )}
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-1.5">
            <span className="truncate text-sm font-semibold text-neutral-900 dark:text-white">
              {isTeam
                ? 'Team room'
                : isFeed
                  ? 'Automations'
                  : isGroup && !room.title.trim()
                  ? 'New chat'
                  : room.title}
            </span>
            {room.working && (
              <Loader2 className="size-3 shrink-0 animate-spin text-neutral-400" />
            )}
          </span>
          <span className="block truncate text-xs text-neutral-500 dark:text-neutral-400">
            {isTeam
              ? 'Chief of Staff + everyone'
              : isFeed
                ? 'Workflows and routines report here'
                : isGroup
                ? 'Chief of Staff + team'
                : room.agent?.name}
            {room.preview && ` · ${room.preview}`}
          </span>
        </span>
        {room.waiting > 0 && (
          <span className="shrink-0 rounded-full bg-amber-500 px-2 text-xs font-bold text-white">
            {room.waiting}
          </span>
        )}
      </button>
      <ChatRoomMenu
        room={room}
        className="absolute right-1.5 top-1/2 -translate-y-1/2 opacity-0 transition-opacity focus:opacity-100 group-hover:opacity-100"
      />
    </div>
  )
}

function Conversation({
  roomId,
  agents,
  historyOpen,
  onToggleHistory,
  onNewChat
}: {
  roomId: number
  agents: TeamAgent[]
  historyOpen: boolean
  onToggleHistory: () => void
  onNewChat: () => void
}) {
  const navigate = useNavigate()
  const { openRecord } = useRecordSheet()
  const room = useQuery({
    queryKey: ['ai-room', roomId],
    queryFn: () => fetchRoom(roomId),
    refetchInterval: (q) =>
      q.state.data?.working ||
      q.state.data?.messages.some((m) => m.card?.generating)
        ? 1500
        : 8000
  })
  const [text, setText] = useState('')
  const [sending, setSending] = useState(false)
  const inFlight = useRef(false)
  const scroller = useRef<HTMLDivElement>(null)
  const data = room.data
  const isTeam = data?.kind === 'team'
  const isFeed = data?.kind === 'feed'
  const multi = isTeam || isFeed || data?.kind === 'group'
  const [panel, setPanel] = useState(
    () => window.matchMedia('(min-width: 1280px)').matches
  )
  // The newest message, not the count: the chat shows the last 200, so the count stops changing.
  const lastId = data?.messages[data.messages.length - 1]?.id ?? 0
  const { items, nested } = useMemo(
    () => groupMessages(data?.messages ?? []),
    [data?.messages]
  )
  // While an agent works but no step is running, it is thinking (talking to the model).
  const stepRunning = data?.messages.some(
    (m) => m.role === 'step' && m.data.status === 'running'
  )
  // What the lead orb shows: thinking, using tools, waiting for you, replying, or at rest.
  const lastAgent = [...(data?.messages ?? [])]
    .reverse()
    .find((m) => m.role === 'agent')
  // "Replying" shows for a few seconds after an answer lands, then switches off on a timer
  // (the room data doesn't change again, so it can't rely on a re-render).
  const [justReplied, setJustReplied] = useState(false)
  const lastAgentId = lastAgent?.id
  const lastAgentAt = lastAgent?.created_at
  useEffect(() => {
    if (!lastAgentAt) return
    const left = 4000 - (Date.now() - new Date(lastAgentAt).getTime())
    if (left <= 0) {
      setJustReplied(false)
      return
    }
    setJustReplied(true)
    const id = window.setTimeout(() => setJustReplied(false), left)
    return () => window.clearTimeout(id)
  }, [lastAgentId, lastAgentAt])
  const chatState: OrbState = data?.working
    ? stepRunning
      ? 'working'
      : 'thinking'
    : (data?.stats.waiting ?? 0) > 0
      ? 'waiting'
      : justReplied
        ? 'replying'
        : 'idle'
  const thinking =
    data?.working && !stepRunning
      ? (data.members.find((m) => m.working)?.name ??
        (data.kind === 'direct' ? data.agent?.name : 'Chief of Staff') ??
        'Agent')
      : null

  useEffect(() => {
    const el = scroller.current
    if (el) el.scrollTop = el.scrollHeight
  }, [lastId, data?.working])

  // "@bil" -> suggest teammates whose key or name starts with it.
  const mention = multi ? /@([\w-]*)$/.exec(text)?.[1] : undefined
  const suggestions = useMemo(() => {
    if (mention === undefined) return []
    const q = mention.toLowerCase()
    return agents
      .filter(
        (a) =>
          a.key.startsWith(q) ||
          a.name.toLowerCase().replace(/\s/g, '').startsWith(q)
      )
      .slice(0, 6)
  }, [agents, mention])

  // One message box for everything. It talks to the team (chat), describes a new workflow (new), or,
  // while a workflow's canvas is open next to the chat, asks for changes to that workflow (edit).
  const [canvasId, setCanvasId] = useState<number | null>(null)
  const [mode, setMode] = useState<'chat' | 'new' | 'edit'>('chat')
  const [canvasDirty, setCanvasDirty] = useState(false)
  const [templatesOpen, setTemplatesOpen] = useState(false)
  // Until the team setup is done, a slim nudge above the box: two minutes of taps saves a lot of questions.
  const memory = useQuery({ queryKey: ['team-memory'], queryFn: fetchMemory, staleTime: 5 * 60_000 })
  const [setupHidden, setSetupHidden] = useState(() => {
    try {
      return localStorage.getItem('ai-team-setup-hidden') === '1'
    } catch {
      return false
    }
  })
  const showSetup = !setupHidden && memory.data?.setup.answered === 0
  const openCanvas = (id: number) => {
    setCanvasId(id)
    setPanel(false)
    setMode('edit')
  }
  const closeCanvas = () => {
    setCanvasId(null)
    setCanvasDirty(false)
    setMode((m) => (m === 'edit' ? 'chat' : m))
  }
  // Shares the canvas's cached workflow (the canvas keeps it fresh), for its name and build status.
  const editing = useQuery({
    queryKey: ['workflow', canvasId],
    queryFn: () => fetchWorkflow(canvasId as number),
    enabled: canvasId !== null
  })
  const editWf = mode === 'edit' && canvasId !== null ? editing.data : undefined
  const editBusy = editWf?.build_status === 'building'
  const blocked =
    mode === 'chat' ? !!data?.working : mode === 'edit' ? !editWf || editBusy || canvasDirty : false
  // When a new workflow card shows up (Chief of Staff started a build), open its canvas.
  const seenCards = useRef<Set<number> | null>(null)
  useEffect(() => {
    if (!data) return
    const cards = data.messages
      .map((m) => m.data.workflow_card)
      .filter((c): c is number => typeof c === 'number')
    if (seenCards.current === null) {
      // Start from a fresh answer, not the cached one, so coming back to an old chat opens nothing.
      if (!room.isFetchedAfterMount) return
      seenCards.current = new Set(cards)
      return
    }
    const fresh = cards.filter((c) => !seenCards.current?.has(c))
    for (const c of cards) seenCards.current.add(c)
    if (fresh.length) openCanvas(fresh[fresh.length - 1])
  }, [data, room.isFetchedAfterMount])

  const [stopping, setStopping] = useState(false)
  async function stop() {
    if (stopping) return
    setStopping(true)
    try {
      await stopRoom(roomId)
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['ai-room', roomId] }),
        queryClient.invalidateQueries({ queryKey: ['ai-rooms'] }),
        queryClient.invalidateQueries({ queryKey: ['workflow'] })
      ])
    } catch (err) {
      notifyError('Could not stop the team', err)
    } finally {
      setStopping(false)
    }
  }

  async function send() {
    const body = text.trim()
    if (!body || sending || blocked || inFlight.current) return
    inFlight.current = true // a double Enter must not send twice (state updates too late for that)
    setSending(true)
    try {
      if (mode === 'new') {
        const wf = await buildWorkflow(body, undefined, roomId)
        seenCards.current?.add(wf.id)
        queryClient.setQueryData(['workflow', wf.id], wf)
        openCanvas(wf.id)
      } else if (mode === 'edit' && canvasId !== null) {
        queryClient.setQueryData(['workflow', canvasId], await buildWorkflow(body, canvasId, roomId))
      } else {
        await sendMessage(roomId, body)
      }
      setText('')
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['ai-room', roomId] }),
        queryClient.invalidateQueries({ queryKey: ['ai-rooms'] })
      ])
    } catch (err) {
      notifyError(
        mode === 'new'
          ? 'Could not start the workflow'
          : mode === 'edit'
            ? 'Could not ask for the change'
            : 'Message not sent',
        err
      )
    } finally {
      inFlight.current = false
      setSending(false)
    }
  }

  function onKey(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      if (suggestions.length > 0) pick(suggestions[0])
      else send()
    }
  }

  function pick(agent: TeamAgent) {
    setText((t) => t.replace(/@([\w-]*)$/, `@${agent.key} `))
  }

  if (room.error)
    return <ErrorState error={room.error} onRetry={() => room.refetch()} />

  const who = multi ? 'Chief of Staff' : (data?.agent?.name ?? '')

  return (
    <WorkflowPanelContext.Provider value={{ openId: canvasId, open: openCanvas }}>
    <div className="flex min-h-0 flex-1 gap-3">
      <div
        className={cn(
          'min-w-0 flex-1 flex-col overflow-hidden [contain:inline-size] rounded-[36px] border border-neutral-200 bg-white dark:border-neutral-800 dark:bg-black',
          canvasId ? 'hidden lg:flex lg:max-w-[460px] lg:min-w-[340px]' : 'flex'
        )}
      >
        <header className="flex items-center gap-3 border-b border-neutral-200 px-4 py-3 dark:border-neutral-800">
          <button
            type="button"
            className={cn(iconBtn, 'md:hidden')}
            onClick={() => navigate(BASE)}
            aria-label="Back to chats"
          >
            <ArrowLeft className="size-4" />
          </button>
          <button
            type="button"
            className={cn(iconBtn, 'hidden md:inline-flex')}
            onClick={onToggleHistory}
            aria-label={historyOpen ? 'Hide chat history' : 'Show chat history'}
            aria-pressed={historyOpen}
            title={historyOpen ? 'Hide chat history' : 'Show chat history'}
          >
            {historyOpen ? (
              <PanelLeftClose className="size-4" />
            ) : (
              <PanelLeftOpen className="size-4" />
            )}
          </button>
          {!historyOpen && (
            <button
              type="button"
              className={cn(iconBtn, 'hidden md:inline-flex')}
              onClick={onNewChat}
              aria-label="New chat"
              title="New chat"
            >
              <Plus className="size-4" />
            </button>
          )}
          {data &&
            (isTeam ? (
              <span className="flex size-9 items-center justify-center rounded-full bg-red-500 text-white">
                <Users className="size-4" />
              </span>
            ) : isFeed ? (
              <span className="flex size-9 items-center justify-center rounded-full bg-neutral-900 text-white dark:bg-white dark:text-black">
                <Zap className="size-4" />
              </span>
            ) : data.kind === 'group' ? (
              <ChiefOrb state={chatState} />
            ) : (
              <AgentAvatar
                agentKey={data.agent?.key}
                name={data.agent?.name}
                state={chatState}
              />
            ))}
          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 items-center gap-2">
              <p className="min-w-0 truncate font-bold text-neutral-900 dark:text-white">
                {isTeam ? 'Team room' : isFeed ? 'Automations' : data?.title}
              </p>
              {chatState !== 'idle' && (
                <span
                  className={cn(
                    'shrink-0 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium',
                    chatState === 'waiting'
                      ? 'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400'
                      : 'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300'
                  )}
                >
                  {STATE_LABEL[chatState]}
                  {chatState === 'waiting' ? '' : '…'}
                </span>
              )}
              {data?.working && (
                <button
                  type="button"
                  onClick={stop}
                  disabled={stopping}
                  className="inline-flex shrink-0 items-center gap-1 rounded-full border border-neutral-200 px-2 py-0.5 text-[11px] font-semibold text-neutral-700 transition-colors hover:border-red-300 hover:text-red-600 disabled:opacity-50 dark:border-neutral-700 dark:text-neutral-300 dark:hover:border-red-500/40 dark:hover:text-red-400"
                >
                  <Square className="size-2.5 fill-current" /> Stop
                </button>
              )}
            </div>
            <p className="truncate text-xs text-neutral-500 dark:text-neutral-400">
              {isTeam
                ? 'Everyone is here. Chief of Staff answers; type @ to ask someone directly.'
                : isFeed
                  ? 'Workflows, routines and scheduled checks report here, so your chats stay quiet.'
                  : data?.kind === 'group'
                  ? `Chief of Staff leads · ${data.members.filter((m) => m.active).length} in this chat · asks before bringing anyone in`
                  : data?.agent?.name}
            </p>
          </div>
          <button
            type="button"
            className={cn(
              iconBtn,
              panel && 'bg-neutral-100 dark:bg-neutral-800'
            )}
            onClick={() => setPanel((p) => !p)}
            aria-label="Show chat details"
            aria-pressed={panel}
            title="Stats, agents and live activity"
          >
            <PanelRight className="size-4" />
          </button>
          {data && data.kind === 'group' && <ChatRoomMenu room={data} />}
          {data && data.kind === 'direct' && data.agent && (
            <>
              <button
                type="button"
                className={iconBtn}
                onClick={() =>
                  openRecord('super_agents__agentprofile', data.agent!.id)
                }
                aria-label="Agent settings"
                title="Agent settings"
              >
                <Settings className="size-4" />
              </button>
              <ChatRoomMenu room={data} />
            </>
          )}
          <button
            type="button"
            className={iconBtn}
            onClick={() => navigate(chatReturnPath())}
            aria-label="Close chat"
            title="Close chat"
          >
            <X className="size-4" />
          </button>
        </header>

        <div
          ref={scroller}
          className="flex-1 overflow-y-auto px-4 py-8 md:px-8"
        >
          <div className="mx-auto w-full max-w-3xl space-y-7">
            {room.isLoading &&
              [0, 1, 2].map((i) => (
                <div
                  key={i}
                  className={cn(
                    'h-16 animate-pulse rounded-[36px] bg-neutral-200 dark:bg-neutral-800',
                    i % 2 ? 'ml-auto w-1/2' : 'w-2/3'
                  )}
                />
              ))}
            {data && data.messages.length === 0 && (
              <div className="mx-auto flex max-w-md flex-col items-center pt-16 text-center">
                <AgentAvatar
                  agentKey={multi ? 'chief_of_staff' : data.agent?.key}
                  name={multi ? 'Chief of Staff' : data.agent?.name}
                  size="xl"
                  state={chatState}
                  className="mb-6"
                />
                <p className="text-lg font-bold text-neutral-900 dark:text-white">
                  {multi ? 'Ask anything' : `Ask ${who} anything`}
                </p>
                <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                  {multi
                    ? 'e.g. "Why are signups down this week?" Chief of Staff works it out and asks before bringing in the agents it needs.'
                    : 'It looks things up and shows you every action as a card to approve before anything changes.'}
                </p>
                {multi && (
                  <button
                    type="button"
                    onClick={() => setTemplatesOpen(true)}
                    className="mt-5 inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-white px-4 py-2 text-sm font-semibold text-neutral-800 transition-all hover:border-neutral-400 active:scale-95 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-100 dark:hover:border-neutral-600"
                  >
                    <LayoutGrid className="size-4" /> Start from a ready-made workflow
                  </button>
                )}
              </div>
            )}
            {items.map((item, index) => {
              // One orb per turn: an answer right under the same agent's steps
              // shares the steps line's orb instead of showing a second one.
              const next = items[index + 1]
              const answer =
                next?.type === 'message' &&
                next.message.role === 'agent' &&
                next.message.agent?.name ===
                  (item.type === 'work' && item.agent)
                  ? next.message
                  : undefined
              const prev = items[index - 1]
              const continued =
                item.type === 'message' &&
                item.message.role === 'agent' &&
                prev?.type === 'work' &&
                prev.agent === item.message.agent?.name
              return item.type === 'work' ? (
                <WorkBlock
                  key={`w${item.key}`}
                  agent={item.agent}
                  steps={item.steps}
                  nested={nested}
                  live={Boolean(data?.working) && index === items.length - 1}
                  answeredAt={answer?.created_at}
                />
              ) : (
                <div
                  key={item.message.id}
                  className="animate-in fade-in-0 slide-in-from-bottom-2 duration-300"
                >
                  <MessageRow
                    message={item.message}
                    roomId={roomId}
                    continued={continued}
                  />
                </div>
              )
            })}
            {thinking && items[items.length - 1]?.type !== 'work' && (
              <ThinkingRow name={thinking} />
            )}
          </div>
        </div>

        <div className="px-4 pb-4 pt-2 md:px-8">
          <div className="relative mx-auto w-full max-w-3xl">
            {suggestions.length > 0 && (
              <div className="absolute bottom-full left-4 mb-2 w-64 overflow-hidden rounded-[36px] border border-neutral-200 bg-white p-1 shadow-xl md:left-0 dark:border-neutral-800 dark:bg-neutral-900">
                {suggestions.map((a) => (
                  <button
                    key={a.key}
                    type="button"
                    onClick={() => pick(a)}
                    className="flex w-full items-center gap-2 rounded-full px-2 py-1.5 text-left text-sm text-neutral-900 hover:bg-neutral-100 dark:text-white dark:hover:bg-neutral-800"
                  >
                    <AgentAvatar agentKey={a.key} name={a.name} size="sm" />
                    <span className="truncate">{a.name}</span>
                    <span className="ml-auto text-xs text-neutral-400">
                      @{a.key}
                    </span>
                  </button>
                ))}
              </div>
            )}
            <DecisionDock roomId={roomId} />
            {showSetup && mode === 'chat' && (
              <div className="mb-2 flex items-center gap-2 rounded-full bg-neutral-100 py-1.5 pl-3 pr-1.5 dark:bg-neutral-900">
                <Brain className="size-4 shrink-0 text-neutral-500 dark:text-neutral-400" />
                <span className="min-w-0 flex-1 truncate text-xs text-neutral-600 dark:text-neutral-300">
                  Tired of explaining things? Teach the team about XOBLACK once, in 2 minutes of taps.
                </span>
                <Link
                  to="/apps/ai-team/knows?setup=1"
                  className="shrink-0 rounded-full bg-neutral-900 px-3 py-1 text-xs font-bold text-white dark:bg-white dark:text-black"
                >
                  Start
                </Link>
                <button
                  type="button"
                  className={cn(iconBtn, 'size-7 shrink-0')}
                  aria-label="Hide"
                  onClick={() => {
                    setSetupHidden(true)
                    try {
                      localStorage.setItem('ai-team-setup-hidden', '1')
                    } catch {
                      // Private mode: it just comes back next time.
                    }
                  }}
                >
                  <X className="size-3.5" />
                </button>
              </div>
            )}
            {mode === 'edit' && canvasId !== null && (
              <div className="mb-2 flex items-center gap-2 px-2">
                <span className="inline-flex min-w-0 items-center gap-1.5 rounded-full bg-neutral-900 px-3 py-1 text-xs font-bold text-white dark:bg-white dark:text-black">
                  <PencilLine className="size-3.5 shrink-0" />
                  <span className="truncate">Changing: {editWf?.name ?? 'workflow'}</span>
                </span>
                <span className="min-w-0 truncate text-xs text-neutral-500 dark:text-neutral-400">
                  {editBusy
                    ? 'The team is working on it…'
                    : canvasDirty
                      ? 'Save your edits on the canvas first.'
                      : editWf?.build_status === 'asking'
                        ? 'Answer the questions above, or say it here.'
                        : 'Tell the team what to change.'}
                </span>
                <button
                  type="button"
                  className={cn(iconBtn, 'ml-auto size-7 shrink-0')}
                  onClick={() => setMode('chat')}
                  aria-label="Talk to the team instead"
                  title="Talk to the team instead"
                >
                  <X className="size-3.5" />
                </button>
              </div>
            )}
            {mode === 'chat' && canvasId !== null && (
              <div className="mb-2 px-2">
                <button
                  type="button"
                  onClick={() => setMode('edit')}
                  className="inline-flex max-w-full items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold text-neutral-600 transition-colors hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800"
                >
                  <PencilLine className="size-3.5 shrink-0" />
                  <span className="truncate">Change “{editing.data?.name ?? 'this workflow'}” instead</span>
                </button>
              </div>
            )}
            {mode === 'new' && (
              <div className="mb-2 flex items-center gap-2 px-2">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-neutral-900 px-3 py-1 text-xs font-bold text-white dark:bg-white dark:text-black">
                  <WorkflowIcon className="size-3.5" /> New workflow
                </span>
                <span className="min-w-0 truncate text-xs text-neutral-500 dark:text-neutral-400">
                  Describe it, or
                </span>
                <button
                  type="button"
                  onClick={() => setTemplatesOpen(true)}
                  className="inline-flex shrink-0 items-center gap-1 rounded-full bg-neutral-100 px-2.5 py-1 text-xs font-semibold text-neutral-800 transition-colors hover:bg-neutral-200 dark:bg-neutral-800 dark:text-neutral-100 dark:hover:bg-neutral-700"
                >
                  <LayoutGrid className="size-3.5" /> pick a ready-made one
                </button>
                <button
                  type="button"
                  className={cn(iconBtn, 'ml-auto size-7')}
                  onClick={() => setMode(canvasId !== null ? 'edit' : 'chat')}
                  aria-label="Back to a normal message"
                >
                  <X className="size-3.5" />
                </button>
              </div>
            )}
            <div className="flex items-end gap-2 rounded-[36px] border border-neutral-200 bg-white py-2 pl-2 pr-2 shadow-sm transition-colors focus-within:border-neutral-400 dark:border-neutral-700 dark:bg-neutral-900 dark:focus-within:border-neutral-500">
              <button
                type="button"
                onClick={() => setMode((m) => (m === 'new' ? (canvasId !== null ? 'edit' : 'chat') : 'new'))}
                aria-pressed={mode === 'new'}
                aria-label="Build a workflow"
                title="Build a workflow on the canvas"
                className={cn(
                  'flex size-9 shrink-0 items-center justify-center rounded-full transition-colors',
                  mode === 'new'
                    ? 'bg-neutral-900 text-white dark:bg-white dark:text-black'
                    : 'text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900 dark:text-neutral-400 dark:hover:bg-neutral-800 dark:hover:text-white'
                )}
              >
                <WorkflowIcon className="size-4" />
              </button>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={onKey}
                rows={Math.min(6, Math.max(1, text.split('\n').length))}
                placeholder={
                  mode === 'new'
                    ? 'e.g. When a chargeback comes in, have Billing check it and ask me before answering'
                    : mode === 'edit'
                      ? editBusy
                        ? 'The team is working on this workflow…'
                        : `e.g. Also email me, or only check posts in English`
                      : data?.working
                      ? `${who} is working…`
                      : `Message ${who}…`
                }
                aria-label="Message"
                className="max-h-40 flex-1 resize-none bg-transparent py-1.5 text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none dark:text-white"
              />
              {data?.working && mode === 'chat' && !text.trim() ? (
                <button
                  type="button"
                  onClick={stop}
                  disabled={stopping}
                  aria-label="Stop the team"
                  title="Stop: agents finish their current step and stop"
                  className="flex size-9 shrink-0 items-center justify-center rounded-full bg-neutral-900 text-white transition-all active:scale-95 disabled:opacity-40 dark:bg-white dark:text-black"
                >
                  {stopping ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <Square className="size-3.5 fill-current" />
                  )}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={send}
                  disabled={!text.trim() || sending || blocked}
                  aria-label="Send"
                  className="flex size-9 shrink-0 items-center justify-center rounded-full bg-neutral-900 text-white transition-all active:scale-95 disabled:opacity-40 dark:bg-white dark:text-black"
                >
                  {sending ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <ArrowUp className="size-4" />
                  )}
                </button>
              )}
            </div>
            <p className="mt-1.5 px-4 text-[11px] text-neutral-400">
              Enter to send · Shift+Enter for a new line · nothing changes until
              you approve a card
            </p>
          </div>
        </div>
      </div>
      {canvasId && (
        <section className="flex min-h-0 min-w-0 flex-[1.6] flex-col" aria-label="Workflow canvas">
          <WorkflowCanvas
            key={canvasId}
            id={canvasId}
            embedded
            onClose={closeCanvas}
            onDirtyChange={setCanvasDirty}
          />
        </section>
      )}
      {panel && data && !canvasId && (
        <aside className="fixed inset-y-3 right-3 z-40 w-[340px] overflow-hidden shadow-2xl xl:static xl:inset-auto xl:z-auto xl:shrink-0 xl:shadow-none rounded-[36px] border border-neutral-200 bg-white dark:border-neutral-800 dark:bg-black">
          <ChatSidePanel
            room={data}
            agents={agents}
            onClose={() => setPanel(false)}
          />
        </aside>
      )}
    </div>
      <TemplatePicker
        open={templatesOpen}
        onOpenChange={setTemplatesOpen}
        room={roomId}
        onAdded={(wf) => {
          seenCards.current?.add(wf.id)
          openCanvas(wf.id)
          queryClient.invalidateQueries({ queryKey: ['ai-room', roomId] })
        }}
      />
    </WorkflowPanelContext.Provider>
  )
}
